import { Button, FileInput, Modal, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import Loader from '../Common/Loader';
import { Reimbursement } from '../../Models/Reimbursement';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { fetchAssetStatuses } from '../../Actions/CommonFields';
import { formatDateInputs, formatNumbers, onlyNumberInput } from '../../Utils';
import { UserLevelNames } from '../../Models/UserLevel';
import { toast } from 'react-toastify';
import ReimbursementApi from '../../Api/ReimbursementApi';
import ViewImage from '../Common/ViewImage';

export default function EditReimbursementModal({
  show,
  setShow,
  selectedReimbursement,
  setselectedReimbursement,
  getReimbursementList,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedReimbursement: Reimbursement | null;
  setselectedReimbursement: React.Dispatch<
    React.SetStateAction<Reimbursement | null>
  > | null;
  getReimbursementList: (() => Promise<void> | undefined) | null;
}) {
  const { assetStatuses } = useAppSelector((state) => state.commonFields);
  const { userModel } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>();
  const [showViewImage, setshowViewImage] = useState<boolean>(false);
  const [viewImageURL, setviewImageURL] = useState<string>('');

  const [formData, setFormData] = useState<Reimbursement>({
    id: 0,
    userId: null,
    statusId: null,
    notes: null,
    totalPrice: null,
    dateOfPurchase: null,
    dateCreated: null,
    imageUrl: null,

    user: null,
    statusName: null,
  });
  const handleSetInitialValues = () => {
    const values = selectedReimbursement
      ? {
          ...selectedReimbursement,
        }
      : {
          id: 0,
          userId: userModel?.id ?? null,
          statusId: 1,
          notes: null,
          totalPrice: null,
          dateOfPurchase: new Date().toISOString().split('T')[0],
          dateCreated: null,
          imageUrl: null,

          user: null,
          statusName: null,
        };
    setFormData(values as Reimbursement);
  };

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      userId: null,
      statusId: null,
      notes: null,
      totalPrice: null,
      dateOfPurchase: null,
      dateCreated: null,
      imageUrl: null,

      user: null,
      statusName: null,
    };
    setFormData(values as Reimbursement);
    setFile(null);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [selectedReimbursement]);

  const getReferences = async () => {
    if (!assetStatuses.length) {
      await Promise.all([dispatch(fetchAssetStatuses())]);
    }
  };

  useEffect(() => {
    if (show) {
      getReferences();
    }
  }, [show]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeCurrencyInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const formattedValue = formatNumbers(value);
    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  function handleImageChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.currentTarget.files) {
      setFile(event.currentTarget.files[0]);
    } else {
      setFile(null);
    }
  }

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!file && !formData.imageUrl) {
      toast.error('Please Upload Reciept');
    } else {
      setButtonDisabled(true);
      setIsLoading(true);
      let reimbursementId = '0';
      let reimbursementUpdated = false;
      let imageUpdated = false;
      await ReimbursementApi.addUpdateReimbursement(formData)
        .then((res) => {
          if (res.data.success) {
            reimbursementId = res.data.affectedEntityIdentifier;
            reimbursementUpdated = true;
          } else {
            toast.error(res.data.message);
            setIsLoading(false);
            setButtonDisabled(false);
          }
        })
        .catch((err) => {
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('reimbursementId', reimbursementId);
        await ReimbursementApi.uploadReimbursementImage(fd)
          .then((res) => {
            if (res.data.success) {
              imageUpdated = true;
            } else {
              toast.error('Error: ' + res.data.message);
            }
          })
          .catch((err) => {
            toast.error('Image Error: ' + err);
          });
      } else {
        imageUpdated = true;
      }
      setIsLoading(false);
      setButtonDisabled(false);
      if (reimbursementUpdated && imageUpdated) {
        toast.success('Success');
        if (getReimbursementList) getReimbursementList();
        onCloseModal();
      }
    }
  };

  function onCloseModal() {
    if (setselectedReimbursement) setselectedReimbursement(null);
    handleResetFormValues();
    setIsLoading(false);
    setShow(false);
  }

  function checkAccess() {
    if (userModel && userModel.userLevelId === UserLevelNames.Driver) {
      return true;
    } else {
      return false;
    }
  }

  const filePreview = useMemo(() => {
    let preview = '';
    if (file) {
      preview = URL.createObjectURL(file);
    }
    return preview;
  }, [file]);

  function viewImage(url: string) {
    setviewImageURL(url);
    setshowViewImage(true);
  }

  return (
    <>
      <Modal show={show} size='3xl' onClose={onCloseModal} popup>
        <Modal.Header></Modal.Header>
        <Modal.Body>
          <div className='relative'>
            {isLoading && <Loader />}
            <div className='w-full'>
              <div className='w-full'>
                <div className='mx-1 flex items-end justify-center'>
                  <div className='flex flex-1 justify-center'>
                    <h1 className='text-gray-900 font-bold text-4xl'>
                      Reimbursement
                    </h1>
                  </div>
                </div>
              </div>
              <div>
                <div className='container mx-auto p-2'>
                  <div className='md:flex no-wrap md:-mx-2 '>
                    <div className='w-full'>
                      <div className='bg-white'>
                        <hr className='bg-gray-300 h-[5px]'></hr>
                        <form id='ReimbursementForm' onSubmit={onSubmit}>
                          <div className='grid grid-cols-[35%_65%] text-xl'>
                            <div className='flex'>
                              <div className='font-bold content-center'>
                                Status:
                              </div>
                            </div>
                            <div className='w-full'>
                              <Select
                                id='statusId'
                                name='statusId'
                                onChange={handleChangeSelect}
                                value={formData.statusId ?? ''}
                                sizing='md'
                                required
                                disabled={checkAccess()}
                              >
                                <option key={1} value={1}>
                                  Pending
                                </option>
                                <option key={2} value={2}>
                                  Paid
                                </option>
                                <option key={3} value={3}>
                                  Canceled
                                </option>
                              </Select>
                            </div>
                          </div>
                          <hr className='bg-gray-300 h-[2px]'></hr>
                          <div className='grid grid-cols-[35%_65%] text-xl'>
                            <div className='flex'>
                              <div className='font-bold content-center'>
                                Total:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='totalPrice'
                                name='totalPrice'
                                type='text'
                                placeholder='0.00'
                                onChange={handleChangeCurrencyInput}
                                onKeyDown={(event) => {
                                  if (onlyNumberInput(event)) {
                                    event.preventDefault();
                                  }
                                }}
                                value={formData.totalPrice ?? ''}
                                sizing='md'
                                addon='$'
                                required
                              />
                            </div>
                          </div>
                          <hr className='bg-gray-300 h-[2px]'></hr>
                          <div className='grid grid-cols-[35%_65%] text-xl'>
                            <div className='flex'>
                              <div className='font-bold content-center'>
                                Purchase Date:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='dateOfPurchase'
                                name='dateOfPurchase'
                                type='date'
                                placeholder='Date...'
                                onChange={handleChange}
                                value={formatDateInputs(
                                  formData.dateOfPurchase ?? ''
                                )}
                                sizing='md'
                                pattern='\d{4}-\d{2}-\d{2}'
                                required
                              />
                            </div>
                          </div>
                          <hr className='bg-gray-300 h-[2px]'></hr>
                          <div className='grid grid-cols-[35%_65%] text-xl'>
                            <div className='flex'>
                              <div className='font-bold content-center'>
                                Notes:
                              </div>
                            </div>
                            <div className='w-full'>
                              <TextInput
                                id='notes'
                                name='notes'
                                type='text'
                                onChange={handleChange}
                                value={formData.notes ?? ''}
                                sizing='md'
                                required
                                autoComplete='new-password'
                              />
                            </div>
                          </div>
                          <hr className='bg-gray-300 h-[2px]'></hr>
                          <div className='grid grid-cols-[35%_65%] text-xl'>
                            <div className='flex'>
                              <div className='font-bold content-center'>
                                Reciept:
                              </div>
                            </div>
                            <div className='w-full'>
                              <FileInput
                                id='file-upload'
                                onChange={handleImageChange}
                                multiple
                                disabled={
                                  formData.imageUrl &&
                                  formData.imageUrl?.length > 0
                                    ? true
                                    : false
                                }
                              />
                            </div>
                          </div>
                          {filePreview && (
                            <div className='flex overflow-auto'>
                              <img
                                key={filePreview}
                                src={filePreview}
                                className='h-28'
                              />
                            </div>
                          )}
                          {formData.imageUrl && (
                            <div
                              className='flex overflow-auto cursor-pointer'
                              onClick={() => viewImage(formData.imageUrl!)}
                            >
                              <img
                                key={formData.id}
                                src={formData.imageUrl}
                                className='h-28'
                              />
                            </div>
                          )}
                          <div className='flex justify-end pt-2'>
                            <Button
                              disabled={buttonDisabled}
                              type='submit'
                              className='buttonMain'
                              form='ReimbursementForm'
                            >
                              Save
                            </Button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <ViewImage
        show={showViewImage}
        setShow={setshowViewImage}
        imageUrl={viewImageURL}
      />
    </>
  );
}
